/**
 * App Component - Main Application Entry
 * 
 * Built with shadcn/ui.
 * Navigation: Home → Trends → News → Prompting → Keitaro → Statistic
 */

import { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom'
import {
  Sun,
  Moon,
  House,
  TrendingUp,
  Newspaper,
  Sparkles,
  Rocket,
  BarChart3,
  User,
  Settings,
  LogOut,
  Bell
} from 'lucide-react'

// Pages
import HomePage from './pages/HomePage/HomePage'
import TrendsPage from './pages/TrendsPage/TrendsPage'
import NewsPage from './pages/NewsPage/NewsPage'
import TrendDetailPage from './pages/HomePage/TrendDetailPage'
import Login from './pages/Login/Login'
import ResearchPage from './pages/ResearchPage/ResearchPage'
import ProfilePage from './pages/ProfilePage/ProfilePage'
import NotificationsPage from './pages/NotificationsPage/NotificationsPage'
import SettingsPage from './pages/SettingsPage/SettingsPage'
import StatisticPage from './pages/StatisticPage/StatisticPage'
import PromptingPage from './pages/PromptingPage/PromptingPage'
import KeitaroPage from './pages/KeitaroPage/KeitaroPage'
import NotFoundPage from './pages/ErrorPages/NotFoundPage'
import ErrorPage from './pages/ErrorPages/ErrorPage'

// Components
import ProtectedRoute from './components/ProtectedRoute'
import { useAuth } from './context/AuthContext'
import { Button } from './components/ui/button'
import { cn } from './lib/utils'
import { Avatar, AvatarFallback } from './components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './components/ui/dropdown-menu'

// Navigation items with icons
const isDev = import.meta.env.VITE_IS_DEV === 'true';

const navItems = [
  { label: 'Home', path: '/', icon: House },
  { label: 'Trends', path: '/trends', icon: TrendingUp },
  { label: 'News', path: '/news', icon: Newspaper },
  { label: 'Prompting', path: '/prompting', icon: Sparkles },
  { label: 'Keitaro', path: '/keitaro', icon: Rocket },
  { label: 'Statistic', path: '/statistic', icon: BarChart3 },
].filter(item => {
  if (['Prompting', 'Keitaro', 'Statistic'].includes(item.label)) {
    return isDev;
  }
  return true;
});

// Header Navigation component
const HeaderNav = () => {
  const location = useLocation()
  const { isAuthenticated } = useAuth()

  // Only show nav if authenticated
  if (!isAuthenticated || location.pathname === '/login') {
    return null
  }

  return (
    <nav className="flex-1 flex items-center gap-1">
      {navItems.map((item) => {
        const Icon = item.icon
        const isActive = location.pathname === item.path
        return (
          <Link
            key={item.label}
            to={item.path}
            className={cn(
              'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors',
              isActive
                ? 'bg-accent text-accent-foreground'
                : 'text-foreground/70 hover:text-foreground hover:bg-accent/50'
            )}
          >
            <Icon className="h-4 w-4" />
            <span className="hidden sm:inline">{item.label}</span>
          </Link>
        )
      })}
    </nav>
  )
}

// Header component
const Header = ({ mode, toggleColorMode }) => {
  const { isAuthenticated, user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  // Don't show header on login page
  if (location.pathname === '/login') {
    return null
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleNavigate = (path) => {
    navigate(path)
  }

  // Get user initials (first 2 letters of username)
  const getInitials = (username) => {
    if (!username) return 'US'
    return username.slice(0, 2).toUpperCase()
  }

  return (
    <header className="flex items-center justify-between px-4 py-4 bg-background">
      {/* Navigation */}
      <div className="flex-1">
        <HeaderNav />
      </div>

      {/* Right: Profile Dropdown */}
      <div className="flex items-center gap-2">
        {isAuthenticated && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 h-9 px-2">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-muted">{getInitials(user?.username)}</AvatarFallback>
                </Avatar>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium">{user?.username || 'User'}</p>
                  <p className="text-xs text-muted-foreground">{user?.role || 'Employee'}</p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => handleNavigate('/profile')}>
                <User className="mr-2 h-4 w-4" />
                Профиль
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleNavigate('/notifications')}>
                <Bell className="mr-2 h-4 w-4" />
                Уведомления
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleNavigate('/settings')}>
                <Settings className="mr-2 h-4 w-4" />
                Настройки
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={toggleColorMode}>
                {mode === 'dark' ? (
                  <Sun className="mr-2 h-4 w-4" />
                ) : (
                  <Moon className="mr-2 h-4 w-4" />
                )}
                Тема: {mode === 'dark' ? 'Темная' : 'Светлая'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Выход
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  )
}

// Footer component
const Footer = () => {
  const { isAuthenticated } = useAuth()
  const location = useLocation()

  // Don't show footer on login page
  if (!isAuthenticated || location.pathname === '/login') {
    return null
  }

  return (
    <footer className="border-t py-4 mt-auto">
      <div className="w-full px-4 text-center">
        <p className="text-sm text-muted-foreground">
          © {new Date().getFullYear()} RM | Trend Research Platform
        </p>
      </div>
    </footer>
  )
}

function App() {
  const [mode, setMode] = useState(() => {
    return localStorage.getItem('colorMode') || 'light'
  })


  // Initialize and update theme
  useEffect(() => {
    // Apply theme class to documentElement
    if (mode === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
    // Save to localStorage
    localStorage.setItem('colorMode', mode)
  }, [mode])

  const toggleColorMode = () => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'))
  }



  return (
    <Router>
      <div className="min-h-screen flex flex-col bg-background text-foreground">
        <Header
          mode={mode}
          toggleColorMode={toggleColorMode}
        />

        <main className="flex-1">
          <div className="w-full px-4 py-6">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route element={<ProtectedRoute />}>
                <Route index element={<HomePage />} />
                <Route path="/trends" element={<TrendsPage />} />
                <Route path="/trend/:query" element={<TrendDetailPage />} />
                <Route path="/news" element={<NewsPage />} />
                <Route path="/research" element={<ResearchPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route
                  path="/settings"
                  element={<SettingsPage mode={mode} toggleColorMode={toggleColorMode} />}
                />

                {/* Dev Only Routes */}
                <Route path="/prompting" element={isDev ? <PromptingPage /> : <Navigate to="/" />} />
                <Route path="/keitaro" element={isDev ? <KeitaroPage /> : <Navigate to="/" />} />
                <Route path="/statistic" element={isDev ? <StatisticPage /> : <Navigate to="/" />} />
              </Route>
              <Route path="/error" element={<ErrorPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </div>
        </main>

        <Footer />
      </div>
    </Router>
  )
}

export default App